import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Container, Button, Form } from 'react-bootstrap';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Organization, updateOrganization } from '../../services/organization/OrganizationService';
import { setOrganization } from '../../../src/slices/organization';
import useOrganizationsList from '../../hooks/api/organization/useOrganizationsList';
import Avatar from '../../components/layout/avatar/Avatar';
import TextSetting from '../registrations/admin/components/properties/settings/TextSetting';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { useHistory } from 'react-router';
import { FallbackPage } from '../../components/layout/navbar/Navbar';
import OptionList from '../../components/library/inputs/optionlist/OptionList';

const Organizations: FC = () => {
  const [organizationName, setName] = useState<string | undefined>('');
  const [currencyOrganization, setCurrency] = useState<string>('usd');
  const [avatarOrganization, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const [organizationSecurity, setOrganizationSecurity] = useState<SecurityModel | null>(null);
  const { getOrganizations } = useOrganizationsList();
  const avatar = useSelector((state: RootState) => state.organization.organization?.avatar);
  const id = useSelector((state: RootState) => state.organization.organization?.id);
  const name: string | undefined = useSelector((state: RootState) => state.organization.organization?.name);
  const currency: string | undefined = useSelector((state: RootState) => state.organization.organization?.currency);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const inputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    setName(name);
    setAvatarUrl(avatar);

    if (currency) {
      setCurrency(currency);
    }
  }, [name, currency]);

  useEffect(() => {
    if (security) {
      setOrganizationSecurity(getSecuritySection(security, SecuritySections.ORGANIZATION));
    }
    if (organizationSecurity && !organizationSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, organizationSecurity]);

  const handleAvatarChange = async (e) => {
    setAvatar(e.target.files[0]);
    const newAvatar = URL.createObjectURL(e.target.files[0]);
    setAvatarUrl(newAvatar);
    setButtonDisabled(false);
  };

  const save = async () => {
    const formData: FormData = new FormData();
    formData.append('Organization', JSON.stringify({ currency: currencyOrganization, name: organizationName, id: id }));
    if (avatarOrganization !== null && avatarOrganization !== undefined) {
      formData.append('Avatar', avatarOrganization as File);
    }
    await updateOrganization(id, formData)
      .then(async (response) => {
        if (response.status == 200) {
          createOrganizationObject(id, organizationName, currencyOrganization, response.data.avatar);
          getOrganizations();
        }
      })
      .catch((e) => {
        console.log(e);
      });
    setButtonDisabled(true);
  };

  const handleUpload = () => {
    inputRef.current?.click();
  };

  const createOrganizationObject = (
    id: string | undefined,
    name: string | undefined,
    currency: string,
    avatar: string,
  ) => {
    const updatedOrganization: Organization = {
      name: name,
      id: id,
      avatar: avatar,
      currency: currency,
    };
    dispatch(setOrganization(updatedOrganization));
  };

  function validateName(value: string) {
    let error;
    value.trim();
    if (!value) {
      setButtonDisabled(true);
      setName('');
      error = intl.formatMessage({ id: 'organizationMenu.modal.errorName' });
    } else {
      setButtonDisabled(false);
      setName(value);
    }
    return error;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <PageTitle title={intl.formatMessage({ id: 'organizationPage.organization' })}></PageTitle>
          <div className="page-body d-flex">
            <div className="image-wrapper d-flex align-items-center justify-content-center">
              <Avatar src={avatarUrl} width={84} height={84}></Avatar>
              <Button variant="primary" size="sm" onClick={handleUpload} disabled={!organizationSecurity?.update}>
                <span className="mdi mdi-camera">
                  <input
                    ref={inputRef}
                    onChange={(e) => handleAvatarChange(e)}
                    className="file-input d-none"
                    type="file"
                  />
                </span>
              </Button>
            </div>
            <div className="name-wrapper d-flex align-items-center mx-4">
              <Form>
                <div className="align-items-center">
                  <div className="form-group col-md-12">
                    <TextSetting
                      value={organizationName}
                      label="organizationPage.empty"
                      size={12}
                      onChange={(val) => {
                        validateName(val);
                      }}
                      readOnlyMode={organizationSecurity?.update == false}
                    />
                    {organizationName == '' && (
                      <div className="name-error">
                        *<FormattedMessage id="organizationPage.nameError"></FormattedMessage>
                      </div>
                    )}
                  </div>
                </div>
                <div className="align-items-center d-flex mt-3">
                  <OptionList
                    value={[currencyOrganization]}
                    options={[
                      { text: 'USD', value: 'usd' },
                      { text: 'CAD', value: 'cad' },
                    ]}
                    onChange={(values) => {
                      if (values[0]) {
                        setCurrency(values[0]);
                        if (organizationName) {
                          validateName(organizationName);
                        }
                      }
                    }}
                    multiSelection={false}
                    className="mx-2"
                  ></OptionList>
                </div>
              </Form>
            </div>
            <div className="button-wrapper d-flex align-items-center">
              <Button variant="primary" size="sm" disabled={buttonDisabled} onClick={save}>
                <span className="mdi mdi-content-save"></span>
                {intl.formatMessage({ id: 'organizationPage.save' })}
              </Button>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Organizations;
